import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { API, useApp } from "@/AppContext";
import { Lightning, MoonStars } from "@phosphor-icons/react";
import { toast } from "sonner";

function formatRemaining(expIso) {
  if (!expIso) return null;
  const ms = new Date(expIso).getTime() - Date.now();
  if (ms <= 0) return "0m";
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export default function FloatingAvailability() {
  const { user, t, authHeaders } = useApp();
  const location = useLocation();
  const [available, setAvailable] = useState(false);
  const [expiresAt, setExpiresAt] = useState(null);
  const [profileExists, setProfileExists] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(async () => {
    if (!user || user.role !== "laborer") return;
    try {
      const r = await axios.get(`${API}/laborer/profile/me`, { headers: authHeaders() });
      if (r.data && r.data.user_id) {
        setProfileExists(true);
        setAvailable(!!(r.data.available_now ?? r.data.available));
        setExpiresAt(r.data.available_expires_at || null);
      } else {
        setProfileExists(false);
      }
    } catch (e) {
      // ignore
    }
  }, [user, authHeaders]);

  useEffect(() => { refresh(); }, [refresh, location.pathname]);

  // Keep both availability controls (this floating button + the dashboard toggle
  // + the calendar auto-save) in sync — any change broadcasts this event.
  useEffect(() => {
    const onChanged = () => refresh();
    window.addEventListener("cn-availability-changed", onChanged);
    return () => window.removeEventListener("cn-availability-changed", onChanged);
  }, [refresh]);

  // Tick every 60s for countdown display
  useEffect(() => {
    if (!available) return;
    const id = setInterval(() => setTick((x) => x + 1), 60000);
    return () => clearInterval(id);
  }, [available]);

  // Auto-refresh from server every 5 minutes to catch server-side auto-expire
  useEffect(() => {
    if (!user || user.role !== "laborer") return;
    const id = setInterval(refresh, 300000);
    return () => clearInterval(id);
  }, [user, refresh]);

  const toggle = async () => {
    if (!profileExists) {
      toast.error(t.laborerForm.liveProfileFirst);
      return;
    }
    setBusy(true);
    try {
      const next = !available;
      const r = await axios.patch(`${API}/laborer/availability`, { available: next }, { headers: authHeaders() });
      setAvailable(!!(r.data.available_now ?? r.data.available));
      setExpiresAt(r.data.available_expires_at || null);
      window.dispatchEvent(new Event("cn-availability-changed"));
      toast.success(next ? t.laborerForm.liveOn : t.laborerForm.liveOff.split(" — ")[0]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    } finally {
      setBusy(false);
    }
  };

  if (!user || user.role !== "laborer") return null;
  // Hide on auth/landing routes to keep them clean
  const hidden = ["/", "/login", "/signup"].includes(location.pathname);
  if (hidden) return null;

  const remaining = available ? formatRemaining(expiresAt) : null;
  // unused tick suppresses warning; ensures re-render
  void tick;

  return (
    <button
      onClick={toggle}
      disabled={busy}
      data-testid="fab-live-toggle"
      aria-label={available ? "Go offline" : "Go live"}
      className={`fixed bottom-6 right-6 z-40 rounded-lg shadow-lg transition-all flex items-center gap-2 px-4 py-3 font-display tracking-tight uppercase text-sm border
        ${available
          ? "bg-emerald-500 text-white border-emerald-400 hover:bg-emerald-400 shadow-emerald-500/40 ring-2 ring-emerald-400/40"
          : "bg-zinc-900 text-zinc-100 border-white/15 hover:bg-[#FF7A1A] hover:text-white hover:border-[#FF7A1A] shadow-black/40"}`}
      style={{ touchAction: "manipulation" }}
    >
      {available ? (
        <>
          <span className="relative flex h-2.5 w-2.5" aria-hidden="true">
            <span className="animate-ping absolute inline-flex h-full w-full bg-white opacity-75"></span>
            <span className="relative inline-flex h-2.5 w-2.5 bg-white"></span>
          </span>
          <span className="flex flex-col items-start leading-tight">
            <span>I'M LIVE</span>
            {remaining && <span className="text-[10px] opacity-80 font-bold">{t.laborerForm.liveExpiresIn} {remaining}</span>}
          </span>
        </>
      ) : (
        <>
          <Lightning size={18} weight="fill" />
          <span>GO LIVE</span>
        </>
      )}
    </button>
  );
}
